/**
 * ============================================================================
 * CAMPANHA BLACK FRIDAY — FUNDO TEMPORÁRIO DA GAMIFICAÇÃO
 * ============================================================================
 *
 * Substitui o fundo azul `#62B3FF` da gamificação pelo visual da campanha
 * "Black Total" (fundo escuro + rabiscos + faixa com logo/título/botão).
 *
 * >>> COMO REVERTER <<<
 * Troque `BLACK_FRIDAY_ENABLED` para `false`. Isso faz TODOS os lugares que
 * usam <GamificationCampaignBg> voltarem automaticamente ao fundo azul
 * `#62B3FF` original, sem precisar editar os componentes de novo.
 *
 * Para remover de vez (depois de revertido):
 *  1. Apague esta pasta (BlackFriday/).
 *  2. Nos arquivos que importam <GamificationCampaignBg>, volte para o
 *     `<div style={{ backgroundColor: '#62B3FF' }} className='...'>` original.
 *  3. Apague `public/black-friday/` e o bloco de fonte Poppins no index.html.
 *
 * Assets ficam em `public/black-friday/` (servidos a partir da raiz "/").
 * Fonte usada: Poppins (var --font-campanha, carregada no index.html).
 * ============================================================================
 */

import React from 'react';
import { useUserStore } from '../../../../Auth/stores/authStore';
import { shouldShowBlack2026 } from '../../../../MyArea/utils/black2026';

/** Flag única de reversão. `false` = volta tudo para o azul #62B3FF. */
export const BLACK_FRIDAY_ENABLED = true;

/** Azul original da gamificação, usado no fallback (campanha desligada). */
const AZUL_ORIGINAL = '#62B3FF';

const FONTE_CAMPANHA = 'font-[family-name:var(--font-campanha)]';

/** Landing page da campanha — destino dos botões (faixa e widget). */
const LINK_CAMPANHA = 'https://aprovatotal.com.br/lp/bf-2026-renovacao/';

const abrirCampanha = () =>
  window.open(LINK_CAMPANHA, '_blank', 'noopener,noreferrer');

/* -------------------------------------------------------------------------- */
/* Faixa (logo + título + botão)                                              */
/* -------------------------------------------------------------------------- */

function Marca({ compacta = false }: { compacta?: boolean }) {
  return (
    <img
      src='/black-friday/pronto/logo.svg'
      alt='Black Total'
      width={289}
      height={145}
      className={
        compacta
          ? 'h-10 w-auto shrink-0 sm:h-14'
          : 'h-[110px] w-auto shrink-0 lg:h-[145px]'
      }
    />
  );
}

function FraseBlack({ soNegrito = false }: { soNegrito?: boolean }) {
  const linha = soNegrito
    ? `${FONTE_CAMPANHA} text-[15px] font-extrabold leading-tight tracking-[-0.03em] text-[#ffffff] [text-shadow:0_0_7px_rgba(0,0,0,0.23)] sm:text-lg`
    : `${FONTE_CAMPANHA} text-[22px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[#ffffff] [text-shadow:0_0_7px_rgba(0,0,0,0.23)] sm:text-[28px]`;
  const subtitulo = soNegrito
    ? `mt-1 ${FONTE_CAMPANHA} text-[11px] font-medium leading-snug text-[#ffffff] [text-shadow:0_0_7px_rgba(0,0,0,0.23)] sm:text-[13px]`
    : `mt-2 ${FONTE_CAMPANHA} text-sm font-medium leading-snug text-[#ffffff] [text-shadow:0_0_7px_rgba(0,0,0,0.23)] sm:text-base`;

  return (
    <>
      <p className={linha}>Seu 2027 está garantido com a gente</p>
      <p className={linha}>
        pelo <span className='text-[#ff5227]'>menor preço do ano</span>.
      </p>
      <p className={subtitulo}>
        Passou na universidade pública? Nós devolvemos parte do investimento!
      </p>
    </>
  );
}

const botaoCampanha = `rounded-[20px] border-[4px] border-solid border-[#050507] bg-[#fff300] px-5 py-2.5 text-center ${FONTE_CAMPANHA} text-base font-bold uppercase leading-tight tracking-tight text-[#050507] transition-colors hover:bg-[#ffe600] sm:text-lg`;

const botaoCampanhaCompacto = `rounded-[16px] border-[2px] border-solid border-[#050507] bg-[#fff300] px-3 py-1.5 text-center ${FONTE_CAMPANHA} text-xs font-bold uppercase leading-tight tracking-tight text-[#050507] transition-colors hover:bg-[#ffe600] sm:px-4 sm:text-sm`;

function BlackFridayFaixa({
  onQuero,
  compacta = false,
}: {
  onQuero?: () => void;
  compacta?: boolean;
}) {
  // Variante compacta (usada no banner global das demais páginas): logo e botão
  // menores e posicionados nas laterais, com o texto centralizado.
  if (compacta) {
    return (
      <section
        aria-label='Campanha Black Total'
        className='relative text-[#ffffff]'
      >
        <div className='absolute left-[169px] top-1/2 z-10 -translate-y-1/2'>
          <Marca compacta />
        </div>
        <div className='absolute right-[163px] top-1/2 z-10 -translate-y-1/2'>
          <button
            type='button'
            onClick={onQuero ?? abrirCampanha}
            className={`${botaoCampanhaCompacto} max-w-[7.5rem] whitespace-normal sm:max-w-none sm:whitespace-nowrap`}
          >
            Quero saber mais
          </button>
        </div>
        <div className='relative mx-auto flex items-center justify-center px-28 py-2 sm:px-56'>
          <div className='text-center'>
            <FraseBlack soNegrito />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label='Campanha Black Total' className='relative text-[#ffffff]'>
      <div className='relative mx-auto flex max-w-[1240px] flex-col items-center gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:justify-between lg:gap-6 lg:py-3'>
        <Marca />
        <div className='max-w-[40rem] text-center'>
          <FraseBlack />
        </div>
        <div className='flex shrink-0 flex-col items-center gap-1.5'>
          <button
            type='button'
            onClick={onQuero ?? abrirCampanha}
            className={botaoCampanha}
          >
            Quero saber mais
          </button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Rabiscos / ícones decorativos (overlay absoluto)                           */
/* -------------------------------------------------------------------------- */

function RiscoSolto({ src, className }: { src: string; className: string }) {
  return (
    <div
      className={`absolute flex h-[30.315px] w-[22.374px] items-center justify-center ${className}`}
    >
      <div className='-scale-y-100 rotate-[155.21deg] skew-x-[2.55deg]'>
        <div className='relative h-[29.554px] w-[9.691px]'>
          <img
            src={src}
            alt=''
            width={19.5628}
            height={37.7577}
            className='absolute left-[-52.88%] top-[-13.69%] h-[37.7577px] w-[19.5628px] max-w-none'
          />
        </div>
      </div>
    </div>
  );
}

function BlackFridayDecor({
  comGame = false,
  compacta = false,
}: {
  comGame?: boolean;
  compacta?: boolean;
}) {
  return (
    <div
      className='pointer-events-none absolute inset-0 overflow-hidden'
      aria-hidden
    >
      <div className='absolute left-1/2 top-0 h-full w-[1440px] -translate-x-1/2'>
        <img
          src='/black-friday/fundo/ellipse-7.svg'
          alt=''
          width={1007}
          height={878}
          className='absolute left-[-589px] top-[-601px] h-[878px] w-[1007px] max-w-none'
        />
        <img
          src='/black-friday/fundo/ellipse-7.svg'
          alt=''
          width={1007}
          height={878}
          className='absolute left-[984px] top-[-1px] h-[878px] w-[1007px] max-w-none'
        />
        <RiscoSolto
          src='/black-friday/so-em-cima/risco-a.svg'
          className={
            compacta
              ? 'left-[62px] top-[14px] origin-top-left scale-75'
              : 'left-[62px] top-[55px]'
          }
        />
        <RiscoSolto
          src='/black-friday/so-em-cima/risco-b.svg'
          className={
            compacta
              ? 'left-[48px] top-[20px] origin-top-left scale-75'
              : 'left-[50px] top-[62px]'
          }
        />
        <img
          src='/black-friday/so-em-cima/rabisco.svg'
          alt=''
          width={60.8283}
          height={59.304}
          className={
            compacta
              ? 'absolute right-6 top-2 h-10 w-10 max-w-none'
              : 'absolute left-[1323px] top-[48px] h-[59.304px] w-[60.8283px] max-w-none'
          }
        />
        {comGame && (
          <>
            <img
              src='/black-friday/so-em-cima/seta.svg'
              alt=''
              width={15.7048}
              height={43.5066}
              className='absolute left-[50px] top-[314px] h-[43.5066px] w-[15.7048px] max-w-none'
            />
            <img
              src='/black-friday/so-em-cima/estrela.svg'
              alt=''
              width={35.2102}
              height={38.9204}
              className='absolute left-[1346px] top-[318px] h-[38.9204px] w-[35.2102px] max-w-none'
            />
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Wrapper público — usado em todos os lugares que tinham #62B3FF             */
/* -------------------------------------------------------------------------- */

interface GamificationCampaignBgProps {
  children: React.ReactNode;
  /** Classes do container (preserva as classes do <div> original). */
  className?: string;
  /** Exibe a faixa (logo + título + botão) no topo. */
  withFaixa?: boolean;
  /** Adiciona os ícones extras (seta/estrela) posicionados para a área do game. */
  comGame?: boolean;
  /** Ação do botão "Quero saber mais" (placeholder por enquanto). */
  onQuero?: () => void;
}

/**
 * Fundo da gamificação. Com a campanha ligada, renderiza o visual Black Friday
 * (fundo escuro + rabiscos + faixa opcional). Com a campanha desligada
 * (BLACK_FRIDAY_ENABLED = false), volta ao fundo azul #62B3FF original.
 */
export function GamificationCampaignBg({
  children,
  className = '',
  withFaixa = false,
  comGame = false,
  onQuero,
}: GamificationCampaignBgProps) {
  const user = useUserStore((state) => state.user);

  // Mesma regra dos banners/modais "Black2026": quem tem produto com expiração
  // em 2027 NÃO recebe a comunicação Black — mantém o fundo azul antigo. Enquanto
  // o usuário não carregou, também mantém o azul (evita piscar o fundo escuro).
  const usarFundoAntigo = !BLACK_FRIDAY_ENABLED || !shouldShowBlack2026(user);

  if (usarFundoAntigo) {
    return (
      <div className={className} style={{ backgroundColor: AZUL_ORIGINAL }}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={`relative isolate overflow-hidden bg-[#101522] ${className}`}
    >
      <BlackFridayDecor comGame={comGame} />
      {withFaixa && (
        <div className='relative z-10'>
          <BlackFridayFaixa onQuero={onQuero} />
        </div>
      )}
      <div className='relative z-10'>{children}</div>
    </div>
  );
}

/**
 * Banner global da campanha (faixa + fundo escuro + rabiscos), para usar abaixo
 * do header em páginas fora da gamificação. Só aparece para usuários elegíveis —
 * MESMA regra "Black2026" (quem expira em 2027 NÃO vê) e respeita a flag.
 */
export function BlackFridayFaixaBanner({ onQuero }: { onQuero?: () => void }) {
  const user = useUserStore((state) => state.user);

  if (!BLACK_FRIDAY_ENABLED || !shouldShowBlack2026(user)) return null;

  return (
    <div className='relative isolate mb-6 overflow-hidden bg-[#101522]'>
      <BlackFridayDecor compacta />
      <div className='relative z-10'>
        <BlackFridayFaixa onQuero={onQuero} compacta />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Widget flutuante (convite de renovação) — fase "black"                     */
/* -------------------------------------------------------------------------- */

const WIDGET_SELO = 'Black Total';
const WIDGET_BOTAO = 'Conferir meu desconto';

// Persiste o estado aberto/fechado do widget. Como o widget é remontado a cada
// navegação/reload (fica no Template de cada página), sem isso ele reabriria
// sozinho toda vez. Depois de fechado, permanece minimizado (só o ícone) até o
// aluno clicar nele. Default (sem chave) = aberto, para aparecer na 1ª vez.
const WIDGET_ABERTO_KEY = 'black2026-widget-aberto';

const lerWidgetAberto = (): boolean => {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(WIDGET_ABERTO_KEY) !== 'false';
};

const salvarWidgetAberto = (aberto: boolean): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(WIDGET_ABERTO_KEY, String(aberto));
};
const BENEFICIOS_RENOVACAO = [
  'Resolve Enem',
  'Esquadrão de Elite',
  'Aprova Universitário',
];

function TextoRenovacao() {
  return (
    <>
      <p
        className={`pr-6 ${FONTE_CAMPANHA} text-[11px] font-bold uppercase tracking-[0.14em] text-[#fff300]`}
      >
        {WIDGET_SELO}
      </p>
      <p
        className={`mt-2 pr-6 ${FONTE_CAMPANHA} text-[22px] font-bold leading-[1.15] tracking-[-0.03em] text-[#ffffff]`}
      >
        Você está no caminho da aprovação!{' '}
        <span className='text-[#fff300]'>E a gente segue junto.</span>
      </p>
      <p className='mt-2 text-left text-sm leading-snug text-[rgba(255,255,255,0.75)]'>
        Como aluno Aprova, você garante sua renovação para 2027 com a melhor
        condição do ano e ganha acesso a:
      </p>
      <ul className='mt-2 list-disc space-y-0.5 pl-4 text-left text-sm font-bold leading-snug text-[#ffffff]'>
        {BENEFICIOS_RENOVACAO.map((beneficio) => (
          <li key={beneficio}>{beneficio}</li>
        ))}
      </ul>
      <p className='mt-2 text-left text-sm leading-snug text-[rgba(255,255,255,0.75)]'>
        Ah, e fique tranquilo, se você passar numa universidade pública, parte
        do investimento volta para o seu bolso! Porque a sua conquista também é
        nossa.
      </p>
    </>
  );
}

/**
 * Widget flutuante da campanha Black (canto inferior direito). Só aparece para
 * usuários elegíveis — MESMA regra dos banners/modais "Black2026": quem tem
 * produto com expiração em 2027 NÃO vê o widget.
 *
 * `onConferir` é placeholder (combinar ação/URL depois).
 */
export function BlackFridayWidget({ onConferir }: { onConferir?: () => void }) {
  const user = useUserStore((state) => state.user);
  // Inicializa a partir do que foi salvo: se o aluno fechou antes, abre
  // minimizado (só o ícone) mesmo após navegar/recarregar.
  const [aberto, setAberto] = React.useState(lerWidgetAberto);

  const fecharWidget = () => {
    setAberto(false);
    salvarWidgetAberto(false);
  };

  const abrirWidget = () => {
    setAberto(true);
    salvarWidgetAberto(true);
  };

  if (!BLACK_FRIDAY_ENABLED || !shouldShowBlack2026(user)) return null;

  return (
    <div className='fixed bottom-5 right-3 z-40 pb-14 sm:bottom-8 sm:right-6'>
      {aberto && (
        <aside
          id='convite-presente'
          aria-label={`Convite ${WIDGET_SELO}`}
          className='relative w-[min(19rem,calc(100vw-1.5rem))] rounded-[22px] border-[2px] border-solid border-[#ff5227] bg-[#101522] px-4 pb-12 pt-4 text-[#ffffff] shadow-[0_16px_40px_rgba(0,0,0,0.45)]'
        >
          <button
            type='button'
            aria-label='Fechar convite'
            onClick={fecharWidget}
            className='absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-[9999px] text-lg leading-none text-[rgba(255,255,255,0.8)] hover:bg-[rgba(255,255,255,0.1)] hover:text-[#ffffff]'
          >
            ×
          </button>
          <TextoRenovacao />
          <button
            type='button'
            onClick={onConferir ?? abrirCampanha}
            className='mt-3 w-full rounded-[9999px] border-[2px] border-solid border-[#050507] bg-[#fff300] px-3 py-2.5 text-center text-sm font-extrabold text-[#050507] transition-colors hover:bg-[#ffe600]'
          >
            {WIDGET_BOTAO}
          </button>
        </aside>
      )}
      <button
        type='button'
        aria-expanded={aberto}
        aria-controls='convite-presente'
        onClick={abrirWidget}
        className='absolute bottom-0 right-0 z-10 w-[89px] pb-5'
      >
        <img
          src='/black-friday/fundo/widget.svg'
          alt=''
          width={89}
          height={85}
          className='mx-auto block'
        />
        <span className='absolute bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap text-center text-[11px] font-extrabold uppercase tracking-wide text-[#ff5227]'>
          {WIDGET_SELO}
        </span>
        <span className='sr-only'>Abrir convite</span>
      </button>
    </div>
  );
}
